/**
 * MXlab — Pont Custom Lab (réimplémentation NATIVE, sans app).
 *
 * Remplace l'extension de thème de l'app custom-lab-shopify-app. Élément
 * `<mx-customlab>` rendu DANS le bloc mx-config (étape 1) ; il lit tout depuis
 * des attributs data- (jamais depuis window.location, cf. handle passé en Liquid)
 * et pilote l'UI de mx-config via le DOM (attribut data-mx-state + points d'ancrage
 * data-mx-cl-*). Aucun fichier natif modifié.
 *
 * Responsabilités :
 *   - persistance localStorage : `mx:{id produit}:custom-model-id` ;
 *
 * ⚠️ LA CLÉ EST L'IDENTIFIANT NUMÉRIQUE, PAS LE HANDLE. Le handle est TRADUIT
 * (`kit-deco-…-sand` en FR, `kove-…-sand` en EN, vérifié) : une config enregistrée
 * depuis la fiche française était rangée sous un nom que la fiche anglaise ne
 * cherchait pas, et disparaissait au changement de langue. C'est le 4e cas de
 * « valeur traduisible utilisée comme clé », après matiere.nom, police.usage et les
 * noms de propriétés de panier. L'identifiant produit est identique dans toutes les
 * langues et ne peut pas être traduit.
 *
 *   - capture de custom_model_id au retour de Custom Lab + NETTOYAGE de l'URL
 *     (history.replaceState) pour qu'une URL partagée ne porte pas la config
 *     d'un autre client ;
 *   - bascule de l'étape 1 en état « enregistrée » (aperçu plein bloc + Modifier +
 *     Supprimer). L'aperçu n'existe QU'ICI : plus AUCUNE injection dans la galerie
 *     média (la galerie garde son comportement natif) ;
 *   - deux propriétés de ligne panier (technique `_custom-model-id` + lisible
 *     « Déco personnalisée ») ajoutées au formulaire, JAMAIS désactivées, et
 *     UNIQUEMENT dans l'état « saved » (exclusivité avec le formulaire simplifié) ;
 *   - détection d'ajout au panier (mx-cart-events → mx:cart:add).
 *
 * TOUT est gardé : si Custom Lab est injoignable ou si localStorage est indisponible
 * (navigation privée), la page reste fonctionnelle — on dégrade en silence.
 */

// Infrastructure Custom Lab (URLs fixes, pas des données métier du thème).
const CUSTOMIZE_BASE = 'https://custom-lab.mxlab.fr/customize';
const IMG_PRE = 'https://firebasestorage.googleapis.com/v0/b/mxlab-customlab.appspot.com/o/Custom%20Models%2F';
const IMG_POST = '.png?alt=media';

class MxCustomLab extends HTMLElement {
  connectedCallback() {
    // Pas de configurateur pour ce produit (métachamp vide / none / aucun) : la carte
    // Custom Lab est déjà rendue désactivée par le Liquid, rien à piloter ici.
    if (this.dataset.has !== 'true') return;

    this.handle = this.dataset.handle || '';
    this.productId = this.dataset.productId || '';
    // Clé de stockage : identifiant numérique, jamais le handle (traduit). Repli sur
    // l'ancienne forme si l'identifiant venait à manquer — mieux vaut une clé fragile
    // que pas de persistance du tout.
    this.storeKey = this.productId ? 'mx:' + this.productId + ':custom-model-id' : this.handle + ':custom-model-id';
    this.formId = this.dataset.formId || '';
    this.config = this.closest('mx-config');
    this.customModelId = null;
    this.imgFailed = false;

    this.onDelete = this.onDelete.bind(this);
    this.onImgError = this.onImgError.bind(this);
    this.onCartAdd = this.onCartAdd.bind(this);

    // Identifiant de CONFIG : depuis l'URL (retour Custom Lab) sinon localStorage.
    let id = null;
    let fromUrl = false;
    try {
      id = new URLSearchParams(window.location.search).get('custom_model_id');
    } catch (e) {
      /* URL illisible : on tentera le localStorage */
    }
    if (id) {
      // MÉMORISÉ AVANT cleanUrl (qui efface le paramètre) : c'est le seul signal fiable
      // d'un RETOUR depuis Custom Lab → seul cas où l'on défile vers l'étape 1.
      fromUrl = true;
      this.lsSet(this.storeKey, id);
      this.cleanUrl();
    } else {
      id = this.lsGet(this.storeKey) || this.migrerAncienneCle();
    }

    // Bouton Supprimer (présent dans la coque « enregistrée »).
    const del = this.config && this.config.querySelector('[data-mx-cl-delete]');
    if (del) del.addEventListener('click', this.onDelete);

    // Les propriétés ne partent QUE dans l'état « saved » : on suit l'état (le client
    // peut ouvrir le formulaire depuis « saved » → état D → il faut les retirer).
    if (this.config && window.MutationObserver) {
      this.stateObs = new MutationObserver(() => this.syncFormInputs());
      this.stateObs.observe(this.config, { attributes: true, attributeFilter: ['data-mx-state'] });
    }

    // Ajout au panier → réévalue l'état « ajoutée au panier ».
    window.addEventListener('mx:cart:add', this.onCartAdd);

    if (id) {
      this.activateSaved(id);
      // Retour depuis Custom Lab (id venu de l'URL) UNIQUEMENT → défile vers l'étape 1.
      if (fromUrl) this.scrollToStep();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('mx:cart:add', this.onCartAdd);
    const del = this.config && this.config.querySelector('[data-mx-cl-delete]');
    if (del) del.removeEventListener('click', this.onDelete);
    if (this.stateObs) this.stateObs.disconnect();
  }

  /**
   * Les anciennes clés possibles pour CE produit, préfixées par le handle.
   *
   * Le handle étant traduit, une config peut avoir été rangée sous celui d'une AUTRE
   * langue. On ne devine pas : les balises `<link rel="alternate" hreflang>` du thème
   * portent l'URL du MÊME produit dans chaque langue publiée, donc tous ses handles.
   * On les lit plutôt que de balayer le localStorage au hasard — un balayage pourrait
   * adopter la config d'un autre produit.
   */
  anciennesCles() {
    const handles = new Set();
    if (this.handle) handles.add(this.handle);
    try {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((l) => {
        const m = (l.getAttribute('href') || '').split('/products/')[1];
        if (m) handles.add(m.split(/[?#/]/)[0]);
      });
    } catch (e) {
      /* pas d'alternates : on se contente du handle courant */
    }
    return [...handles].map((h) => h + ':custom-model-id').filter((k) => k !== this.storeKey);
  }

  /**
   * Migration : lit l'ancienne clé, la réécrit sous la nouvelle, et efface l'ancienne.
   * Sans ça, un client qui a une configuration en cours la perd au déploiement.
   */
  migrerAncienneCle() {
    for (const k of this.anciennesCles()) {
      const v = this.lsGet(k);
      if (v) {
        this.lsSet(this.storeKey, v);
        this.lsRemove(k);
        return v;
      }
    }
    return null;
  }

  /* ---------------------------------------------------------------- localStorage */
  lsGet(k) {
    try {
      return window.localStorage.getItem(k);
    } catch (e) {
      return null;
    }
  }
  lsSet(k, v) {
    try {
      window.localStorage.setItem(k, v);
    } catch (e) {
      /* navigation privée / quota : on continue sans persistance */
    }
  }
  lsRemove(k) {
    try {
      window.localStorage.removeItem(k);
    } catch (e) {}
  }

  /* --------------------------------------------------------------------- helpers */
  imageUrl(id) {
    return IMG_PRE + encodeURIComponent(id) + IMG_POST;
  }

  cleanUrl() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('custom_model_id');
      window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    } catch (e) {
      /* replaceState indisponible : sans gravité, l'URL garde juste le paramètre */
    }
  }

  /**
   * Défile vers l'étape 1 au retour de Custom Lab. On vise le HAUT du bloc (plaque orange
   * + titre `.mx-step`), PAS l'aperçu, pour que le client voie tout le contexte. Décalage
   * de la hauteur de l'en-tête collant du thème (`--header-group-height`, posé sur <body>
   * par critical.js) pour que le titre ne soit pas coincé dessous. Le titre étant ancré en
   * HAUT du viewport, la barre d'achat collante (bas, mobile) ne peut pas le masquer.
   *
   * « Bloc réellement rendu » : `<mx-customlab>` est le DERNIER enfant de `<mx-config>` et
   * le module est différé (parsing terminé) → le bloc existe déjà. Les DEUX rAF laissent le
   * navigateur calculer la mise en page avant de MESURER (sinon position fausse). Le scroll
   * est asynchrone → il ne bloque ni ne retarde JAMAIS l'affichage de la page.
   *
   * ⚠️ ON NE NOMME PAS LE CONTENEUR DÉFILANT — cf. piège 37 de CLAUDE.md.
   * Cette méthode calculait autrefois une position absolue et appelait `window.scrollTo()`.
   * Horizon 4.1.3 a introduit `<div class="page-wrapper">` (layout/theme.liquid) qui devient
   * le conteneur défilant AU-DESSUS DE 990px, avec `html` et `body` en `overflow: hidden`
   * (base.css). Résultat mesuré : `window.pageYOffset` vaut TOUJOURS 0 et `window.scrollTo()`
   * est SANS EFFET sur desktop — en silence, rien ne casse, le défilement ne se produit pas.
   * `scrollIntoView` ne nomme aucun conteneur : il agit sur le plus proche ancêtre défilant,
   * quel qu'il soit. Le décalage sous l'en-tête collant passe par `scroll-margin-top`, que le
   * navigateur applique lui-même — donc plus aucun calcul de position de notre côté.
   */
  scrollToStep() {
    const target = (this.config && this.config.querySelector('.mx-step')) || this.config;
    if (!target) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduce ? 'auto' : 'smooth';
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const rect = target.getBoundingClientRect();
        if (!rect.height) return; // pas encore rendu (sécurité) : on ne devine pas une position
        // Marge de défilement = hauteur de l'en-tête collant + une respiration de 16px.
        // Les replis reproduisent l'ancienne chaîne : --header-group-height, puis
        // --header-height, puis 72px.
        target.style.scrollMarginTop =
          'calc(var(--header-group-height, var(--header-height, 72px)) + 16px)';
        try {
          target.scrollIntoView({ block: 'start', behavior });
        } catch (e) {
          target.scrollIntoView(true); // très vieux navigateurs : saut simple
        }
      })
    );
  }

  /* --------------------------------------------------------------- état « saved » */
  activateSaved(id) {
    this.customModelId = id;
    if (!this.config) return;

    const idEl = this.config.querySelector('[data-mx-config-id]');
    if (idEl) idEl.textContent = id;

    // Modifier pointe TOUJOURS vers la CONFIG (jamais le modèle) : modifier renvoie le
    // MÊME identifiant → aucune ligne de panier ne devient périmée, pas de synchro.
    const mod = this.config.querySelector('[data-mx-cl-modify]');
    if (mod) mod.href = CUSTOMIZE_BASE + '/' + encodeURIComponent(id) + '?skip_save=true';

    const bug = this.config.querySelector('[data-mx-cl-imgbug]');
    if (bug) bug.hidden = true; // placeholder d'échec masqué tant que l'image n'a pas échoué
    const thumb = this.config.querySelector('[data-mx-cl-thumb]');
    if (thumb) {
      // display inline : le reset `img { display:block }` du thème l'emporte sur [hidden].
      thumb.hidden = false;
      thumb.style.display = '';
      thumb.addEventListener('error', this.onImgError, { once: true });
      thumb.src = this.imageUrl(id);
    }

    this.config.dataset.mxState = 'saved';
    this.syncFormInputs();
    this.checkCartForConfig();
  }

  onDelete() {
    this.lsRemove(this.storeKey);
    this.anciennesCles().forEach((k) => this.lsRemove(k));
    this.removeFormInputs();
    this.customModelId = null;
    this.imgFailed = false;
    const bug = this.config && this.config.querySelector('[data-mx-cl-imgbug]');
    if (bug) bug.hidden = true; // on NE vide PAS le texte (statique, rendu en Liquid)
    const thumb = this.config && this.config.querySelector('[data-mx-cl-thumb]');
    if (thumb) {
      thumb.hidden = true;
      thumb.style.display = 'none';
      thumb.removeAttribute('src');
    }
    // Restaure l'état initial (les deux cartes de choix) SANS recharger la page.
    if (this.config) this.config.dataset.mxState = 'choice';
  }

  /* --------------------------------------------------------- propriétés panier */
  // Les propriétés ne s'attachent QUE dans l'état « saved » (le chemin Custom Lab).
  // État D (formulaire ouvert depuis « saved ») : state !== 'saved' → on les retire, ce
  // sont les champs du formulaire qui partent. La config reste en localStorage (mise de
  // côté, pas supprimée) → le retour vers « saved » les réattache.
  syncFormInputs() {
    const active = this.config && this.config.dataset.mxState === 'saved' && this.customModelId;
    if (active) this.ensureFormInputs();
    else this.removeFormInputs();
  }

  makeInput(name) {
    const i = document.createElement('input');
    i.type = 'hidden';
    i.name = name;
    // JAMAIS disabled : un input désactivé n'est pas soumis (piège connu).
    if (this.formId) i.setAttribute('form', this.formId);
    return i;
  }

  ensureFormInputs() {
    if (!this.formInputs) {
      // ⚠️ `_custom-model-id` est une CLÉ, pas une étiquette d'affichage : ce nom est RELU
      // par du code (checkCartForConfig, plus bas) pour retrouver la ligne de panier.
      // Il ne doit JAMAIS devenir un réglage de bloc. Un réglage `type: "text"`
      // matérialisé dans une template JSON devient traduisible par Translate & Adapt ; la
      // clé serait alors traduite sur les parcours non francophones (~40 % du trafic) et
      // deviendrait introuvable — l'identifiant de configuration serait perdu sur la
      // commande. La protection est précisément de rester un LITTÉRAL ici : Translate &
      // Adapt ne sait traduire que les fichiers de locale, les réglages de gabarits JSON
      // et les ressources boutique — pas une chaîne dans un `.js`.
      // Détail et arbitrage : piège 31 de CLAUDE.md. Les 13 AUTRES propriétés de panier
      // sont, elles, des étiquettes visibles et se traduisent normalement.
      const tech = this.makeInput('properties[_custom-model-id]');
      const vis = this.makeInput('properties[' + (this.dataset.visibleProp || 'Déco personnalisée') + ']');
      this.formInputs = [tech, vis];
      this.append(tech, vis);
    }
    this.formInputs[0].value = this.customModelId || '';
    this.formInputs[1].value = this.dataset.visibleValue || 'Oui';
  }

  removeFormInputs() {
    if (this.formInputs) {
      this.formInputs.forEach((i) => i.remove());
      this.formInputs = null;
    }
  }

  /* -------------------------------------------------------------------- aperçu */
  // Image introuvable (problème de génération, pas un délai → AUCUN retry) : dans l'aperçu
  // de l'étape 1 on remplace l'image par le placeholder de MÊME cadre (fond teinté). Le
  // reste du bloc ne bouge pas : la config reste valide, seul son aperçu manque.
  onImgError() {
    if (this.imgFailed) return;
    this.imgFailed = true;
    const thumb = this.config && this.config.querySelector('[data-mx-cl-thumb]');
    if (thumb) {
      thumb.hidden = true;
      thumb.style.display = 'none';
    }
    const bug = this.config && this.config.querySelector('[data-mx-cl-imgbug]');
    if (bug) bug.hidden = false; // texte court statique (Liquid) — rien à remplir
  }

  /* --------------------------------------------------------------------- panier */
  checkCartForConfig() {
    if (!this.customModelId) return;
    try {
      fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          // ⚠️ Le SEUL endroit du thème qui lit une propriété de panier PAR SON NOM.
          // `_custom-model-id` est donc une CLÉ, pas une étiquette : elle doit rester
          // IDENTIQUE à celle écrite dans ensureFormInputs(), et ne JAMAIS devenir un
          // réglage de bloc. Un réglage `type: "text"` matérialisé dans une template JSON
          // devient traduisible ; traduite, la clé ne correspondrait plus et la
          // configuration passerait pour absente du panier sur les parcours non
          // francophones. Voir piège 31 de CLAUDE.md.
          const inCart = (cart.items || []).some(
            (it) => it.properties && it.properties['_custom-model-id'] === this.customModelId
          );
          if (inCart) this.markAddedToCart();
        })
        .catch(() => {});
    } catch (e) {
      /* Shopify.routes indisponible : sans gravité */
    }
  }

  // Déjà au panier : seul le TEXTE de la colonne signale l'état. Le bouton NE change PAS
  // (« Modifier la configuration », même URL de config) — modifier renvoie le même id,
  // aucune ligne ne devient périmée. Pour repartir de zéro, le client passe par Supprimer.
  markAddedToCart() {
    if (!this.config) return;
    const title = this.config.querySelector('[data-mx-cl-title]');
    if (title && this.dataset.addedTitle) title.textContent = this.dataset.addedTitle;
    const text = this.config.querySelector('[data-mx-cl-text]');
    if (text && this.dataset.addedText) text.textContent = this.dataset.addedText;
  }

  onCartAdd() {
    if (this.customModelId) this.checkCartForConfig();
  }
}

if (!customElements.get('mx-customlab')) {
  customElements.define('mx-customlab', MxCustomLab);
}
